import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';

const HomeSection: React.FC = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [badgeTopOffset, setBadgeTopOffset] = useState<number | null>(null);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const syncBadgeAlignment = () => {
      if (badgeRef.current && sectionRef.current && window.innerWidth >= 1024) {
        const sectionRect = sectionRef.current.getBoundingClientRect();
        const badgeRect = badgeRef.current.getBoundingClientRect();
        const offset = badgeRect.top - sectionRect.top;
        setBadgeTopOffset(offset);
      } else {
        setBadgeTopOffset(null);
      }
    };

    syncBadgeAlignment();
    const timer = setTimeout(syncBadgeAlignment, 150);

    window.addEventListener('resize', syncBadgeAlignment);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', syncBadgeAlignment);
    };
  }, []);

  // =================================================================
  // --- LOGIKA USER & AUTH ---
  // =================================================================

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userName = user?.name || user?.username || "Student";

  // =================================================================
  // --- HANDLERS (LOGIKA TOMBOL) ---
  // =================================================================

  const handleExploreClick = () => {
    navigate('/program-selection');
  };

  const handleConsultClick = () => {
    if (!user) {
      alert("Please login to consult.");
      navigate('/auth/login');
    } else {
      const waNumber = "6287729949195";
      const waMessage = `Hello Fluenesia, my name is ${userName}. I would like to consult regarding the Indonesian language programs.`;
      const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;
      window.open(waUrl, '_blank');
    }
  };

  return (
    <>
      <div className="w-full bg-[#F4F5F7] overflow-hidden">
        <section
          ref={sectionRef}
          className="bg-[#F4F5F7] relative w-full overflow-hidden min-h-0 lg:h-[560px] flex items-center font-montserrat"
        >

          {/* ================================================================= */}
          {/* --- DESKTOP CANVAS CONTAINER (1440px) --- */}
          {/* ================================================================= */}
          <div className="relative w-full max-w-[1440px] h-full mx-auto flex items-center">

            {/* 1. Rectangle 30: Pink Shape Background (Desktop) */}
            <div
              className="pointer-events-none select-none absolute bg-[#FEE6E6] rounded-[100px] z-0 hidden lg:block"
              style={{
                width: '939.71px',
                height: '760.04px',
                left: '870px',
                top: '-83px',
                transform: 'rotate(-30deg)',
                transformOrigin: 'center center'
              }}
              aria-hidden="true"
            />

            {/* 2. Rectangle 29: Red Shape (Desktop) */}
            <div
              className="pointer-events-none select-none absolute bg-[#C60302] rounded-[100px] z-1 hidden lg:block"
              style={{
                width: '816.92px',
                height: '760.04px',
                left: '820px',
                top: '247px',
                transform: 'rotate(-30deg)',
                transformOrigin: 'center center'
              }}
              aria-hidden="true"
            />

            {/* Geometri Mobile & Tablet Background */}
            <div
              className="pointer-events-none select-none absolute -bottom-[120px] -right-[100px] w-[520px] h-[480px] sm:w-[680px] sm:h-[580px] bg-[#C60302] rounded-[80px] -rotate-[25deg] z-0 lg:hidden opacity-90"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none select-none absolute -top-[80px] -right-[80px] w-[360px] h-[360px] bg-[#FEE6E6] rounded-[60px] -rotate-[25deg] z-0 lg:hidden"
              aria-hidden="true"
            />

            <div className="relative lg:absolute lg:left-[48px] lg:top-0 lg:bottom-0 lg:flex lg:flex-col lg:justify-center z-20 w-full lg:w-auto py-8 lg:py-0">
              <div
                className="w-full lg:w-[740px] xl:w-[790px] px-6 sm:px-12 lg:px-0 flex flex-col items-start gap-4 lg:gap-5"
                data-aos="fade-right"
              >

                {/* 1. Rating Badge */}
                <div
                  ref={badgeRef}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 lg:px-4 lg:py-1.5 w-auto bg-[#FDFDFD] border border-[#C60302] rounded-full shadow-[2px_2px_4px_rgba(32,32,32,0.15)]"
                >
                  <svg className="w-3.5 h-3.5 fill-[#C60302] flex-shrink-0" viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                  <span className="font-poppins font-semibold text-[11px] lg:text-[12px] leading-[16px] text-[#202020] tracking-wide select-none">
                    4.9 Satisfaction Rate
                  </span>
                </div>

                {/* 2. Headline & Subtitle Container */}
                <div className="flex flex-col items-start gap-3 lg:gap-3.5 w-full">
                  <h1 className="font-montserrat font-bold text-[32px] sm:text-[40px] lg:text-[52px] xl:text-[54px] leading-[1.15] text-[#202020] tracking-tight">
                    Start Your Indonesian <br className="hidden sm:inline" />
                    Language Journey <br className="hidden sm:inline" />
                    With Fluenesia
                  </h1>

                  <p className="font-montserrat font-normal text-[14px] sm:text-[15px] lg:text-[17px] xl:text-[18px] leading-[24px] lg:leading-[27px] text-[#6A6A6A] max-w-[560px] lg:max-w-[630px]">
                    Fluenesia is an online learning platform helping international students master Indonesian through structured, level-based lessons and immersive cultural learning.
                  </p>
                </div>

                {/* 3. Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto pt-1">
                  {/* Button 1: Explore All Programs */}
                  <button
                    id="hero-explore-programs-btn"
                    onClick={handleExploreClick}
                    className="w-full sm:w-auto px-6 lg:px-8 h-[44px] lg:h-[48px] inline-flex items-center justify-center bg-[#C60302] hover:bg-[#a50201] text-white font-montserrat font-bold text-[14px] lg:text-[15px] leading-[18px] rounded-[8px] shadow-[2px_2px_4px_rgba(32,32,32,0.15)] hover:shadow-lg transition-all duration-200 cursor-pointer active:scale-95"
                  >
                    Explore All Programs
                  </button>

                  {/* Button 2: I Want To Consult */}
                  <button
                    id="hero-consult-btn"
                    onClick={handleConsultClick}
                    className="w-full sm:w-auto px-6 lg:px-8 h-[44px] lg:h-[48px] inline-flex items-center justify-center bg-[#FDFDFD] hover:bg-red-50/50 border-2 border-[#C60302] text-[#C60302] font-montserrat font-bold text-[14px] lg:text-[15px] leading-[18px] rounded-[8px] shadow-[2px_2px_4px_rgba(32,32,32,0.15)] hover:shadow-md transition-all duration-200 cursor-pointer active:scale-95"
                  >
                    I Want To Consult
                  </button>
                </div>

              </div>
            </div>

            <div
              className="relative z-10 w-full lg:w-[559px] h-[340px] sm:h-[420px] lg:h-auto flex justify-center lg:justify-end items-end pointer-events-none select-none lg:absolute lg:right-4 xl:right-12 lg:left-auto lg:bottom-0"
              style={
                badgeTopOffset !== null
                  ? { top: `${badgeTopOffset}px`, height: `calc(100% - ${badgeTopOffset}px)` }
                  : undefined
              }
              data-aos="fade-left"
            >
              <img
                src="/hero-tutors.png"
                alt="Fluenesia Indonesian Language Tutors"
                className="w-auto h-full max-h-full object-contain object-top drop-shadow-xl transform scale-[1.08] origin-bottom"
              />
            </div>

          </div>
        </section>
      </div>
    </>
  );
};

export default HomeSection;